import { usePage } from '@inertiajs/react'
import type { SharedProps, Translations } from '@/types'

export type TranslateVars = Record<string, string | number>

/** Language endonyms — each shown in its own language regardless of UI locale. */
export const LANGUAGE_NAMES: Record<string, string> = { en: 'English', de: 'Deutsch' }

/** Replace Rails-style %{name} placeholders. */
function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template
  return template.replace(/%\{(\w+)\}/g, (_match, name) =>
    name in vars ? String(vars[name]) : `%{${name}}`,
  )
}

/**
 * Pick the right plural form from a `{ zero?, one, other, … }` node, given a
 * count and locale. `zero` is an explicit override (e.g. "No users"); the rest
 * follow CLDR categories via Intl.PluralRules.
 */
function selectPlural(node: Translations, count: number, locale: string): string | undefined {
  if (count === 0 && typeof node.zero === 'string') return node.zero

  const category = new Intl.PluralRules(locale).select(count)
  const candidate = node[category] ?? node.other
  return typeof candidate === 'string' ? candidate : undefined
}

/**
 * Look up a dot-path key (e.g. "nav.dashboard") in the translation catalog.
 * Returns the key itself when missing, so untranslated strings are visible
 * rather than blank. When the key resolves to a plural node (an object) and a
 * numeric `count` var is given, the correct form is selected for `locale`.
 * Pure — unit-tested without an Inertia context.
 */
export function translate(
  translations: Translations,
  key: string,
  vars?: TranslateVars,
  locale = 'en',
): string {
  const value = key.split('.').reduce<string | Translations | undefined>((acc, part) => {
    if (acc && typeof acc === 'object') return acc[part]
    return undefined
  }, translations)

  if (typeof value === 'string') return interpolate(value, vars)

  if (value && typeof value === 'object' && typeof vars?.count === 'number') {
    const form = selectPlural(value, vars.count, locale)
    if (form !== undefined) return interpolate(form, vars)
  }

  return key
}

/** Hook giving the current locale and a bound `t()` for page/layout components. */
export function useTranslations() {
  const { props } = usePage<SharedProps>()
  const { translations, locale } = props

  return {
    locale,
    t: (key: string, vars?: TranslateVars) => translate(translations, key, vars, locale),
  }
}
